import type { ApiClient } from '../client';
import type { ApiError } from '../types';

const BASE = '/loot_machine/api/game_platform/iframe/v1';

/**
 * Outcome of a `POST wallet/withdraw`. The GP Connect payout is a *dojima*
 * multi-step flow: the first call may come back needing KYC or Connect
 * onboarding before any money can move. We model all three server responses as
 * one non-throwing result so the caller can branch without a try/catch:
 *  - `ok` + `balanceCents` — the payout was queued, ledger debited.
 *  - `setupAccountUrl` — no completed Stripe Identity KYC yet; open this hosted
 *    URL, finish verification, then retry the withdrawal.
 *  - `onboardingUrl` — Connect account not payout-ready; open this hosted
 *    onboarding URL (bank details), then retry.
 *  - neither URL — a plain failure; `message` explains why.
 */
export interface WithdrawResult {
  ok: boolean;
  setupAccountUrl?: string | undefined; // KYC (Stripe Identity) hosted URL
  onboardingUrl?: string | undefined; // Connect Express onboarding URL
  balanceCents?: number | undefined;
  expressAccount?: string | undefined; // Stripe Express dashboard login link
  message?: string | undefined;
}

export async function createWithdraw(
  client: ApiClient,
  amountDollars: number,
): Promise<WithdrawResult> {
  try {
    const raw = await client.request<{
      result?: { balance_cents?: number; express_account?: string };
    }>(`${BASE}/wallet/withdraw`, { method: 'POST', body: { amount: amountDollars } });
    return {
      ok: true,
      balanceCents: raw.result?.balance_cents ?? 0,
      expressAccount: raw.result?.express_account,
    };
  } catch (e) {
    // The 403 gate bodies carry `setup_account_url` / `link` alongside the
    // error_detail; `parseError` threads the whole body onto `ApiError.data`.
    const err = e as ApiError & { data?: { setup_account_url?: string; link?: string } };
    return {
      ok: false,
      setupAccountUrl: err.data?.setup_account_url,
      onboardingUrl: err.data?.link,
      message: err.message,
    };
  }
}

export async function createKycSession(client: ApiClient) {
  const raw = await client.request<{ result?: { url?: string } }>(
    `${BASE}/wallet/kyc_session`,
    { method: 'POST' },
  );
  return { url: raw.result?.url ?? '' };
}
